import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

const QuestionModal = ({ show, handleClose, onSave, selectedQuestion }) => {
  const [formData, setFormData] = useState({
    questionText: '',
    options: ['', '', '', ''],
    correctAnswer: '',
    section: '',
    customSection: '',
    difficulty: 'easy'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedQuestion) {
      // Initialize form with selected question data
      setFormData({
        questionText: selectedQuestion.questionText || selectedQuestion.text || '',
        options: selectedQuestion.options || ['', '', '', ''],
        correctAnswer: selectedQuestion.correctAnswer || '',
        section: selectedQuestion.section || selectedQuestion.part || '',
        customSection: '',
        difficulty: selectedQuestion.difficulty || 'easy'
      });
      setErrors({});
    } else {
      // Reset form for new question
      setFormData({
        questionText: '',
        options: ['', '', '', ''],
        correctAnswer: '',
        section: '',
        customSection: '',
        difficulty: 'easy'
      });
      setErrors({});
    }
  }, [selectedQuestion, show]);

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'section' && value !== 'other') {
      setFormData(prev => ({ ...prev, customSection: '' }));
      setErrors(prev => ({ ...prev, customSection: '' }));
    }
  };

  const handleCustomSectionChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, customSection: value });
    setErrors(prev => ({ ...prev, customSection: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.questionText.trim()) {
      newErrors.questionText = 'Question text is required';
    }
    const nonEmptyOptions = formData.options.filter(opt => opt.trim() !== '');
    if (nonEmptyOptions.length < 2) {
      newErrors.options = 'Please provide at least 2 options';
    }
    if (!formData.correctAnswer || !nonEmptyOptions.includes(formData.correctAnswer)) {
      newErrors.correctAnswer = 'Please select a valid correct answer from the options';
    }
    if (!formData.section) {
      newErrors.section = 'Section is required';
    } else if (formData.section === 'other' && !formData.customSection.trim()) {
      newErrors.customSection = 'Custom section is required';
    }
    if (!formData.difficulty) {
      newErrors.difficulty = 'Difficulty is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const nonEmptyOptions = formData.options.filter(opt => opt.trim() !== '');
    const sectionToSubmit = formData.section === 'other' ? formData.customSection.trim() : formData.section;

    const data = {
      questionText: formData.questionText.trim(),
      options: nonEmptyOptions,
      correctAnswer: formData.correctAnswer.trim(),
      section: sectionToSubmit,
      difficulty: formData.difficulty
    };

    onSave(data);
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData({ ...formData, options: [...formData.options, ''] });
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 1) {
      const newOptions = [...formData.options];
      newOptions.splice(index, 1);
      
      // Clear correct answer if it was the removed option
      const removedOption = formData.options[index];
      const newCorrectAnswer = formData.correctAnswer === removedOption ? '' : formData.correctAnswer;
      
      setFormData({ 
        ...formData, 
        options: newOptions,
        correctAnswer: newCorrectAnswer
      });
      setErrors(prev => ({ ...prev, options: '' }));
    }
  };

  const styles = {
    modalContent: {
      backgroundColor: 'white', // Fixed typo: backgroundcolor to backgroundColor
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      color: '#1F2937',
      fontFamily: "'Roboto', sans-serif",
      width: '100%',
      maxWidth: '900px',
      margin: '0 auto'
    },
    modalHeader: {
      borderBottom: 'none',
      padding: '0.75rem 1rem'
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1F2937'
    },
    modalBody: {
      padding: '1rem',
      maxHeight: '70vh',
      overflowY: 'hidden'
    },
    formLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem'
    },
    formControl: {
      backgroundColor: '#ffffff',
      color: '#1F2937',
      border: '1px solid #D1D5DB',
      borderRadius: '0.25rem',
      fontSize: '0.875rem',
      padding: '0.4rem'
    },
    formSelect: {
      backgroundColor: '#ffffff',
      color: '#1F2937',
      border: '1px solid #D1D5DB',
      borderRadius: '0.25rem',
      fontSize: '0.875rem',
      padding: '0.4rem'
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1rem', // Increased gap between rows (line space between first and second row)
      marginBottom: '0.5rem'
    },
    optionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem', // Increased gap between options in the same row (e.g., Option 1 and Option 2)
      marginBottom: '0.25rem'
    },
    sectionDifficultyGrid: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap'
    },
    sectionField: {
      flex: '1',
      minWidth: '200px'
    },
    difficultyField: {
      flex: '1',
      minWidth: '200px'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 16px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease'
    },
    cancelButton: {
      backgroundColor: '#D1D5DB',
      color: '#374151',
      padding: '6px 16px',
      border: 'none',
      borderRadius: '30px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    addOptionButton: {
      fontSize: '0.875rem',
      padding: '0.25rem 0.5rem'
    }
  };

  // Split options into two rows
  const optionsPerRow = Math.ceil(formData.options.length / 2);
  const firstRowOptions = formData.options.slice(0, optionsPerRow);
  const secondRowOptions = formData.options.slice(optionsPerRow);

  return (
    <>
      <style>
        {`
          @keyframes glow {
            0% { box-shadow: 0 0 12px rgba(100, 150, 255, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
            50% { box-shadow: 0 0 18px rgba(100, 150, 255, 1), 0 0 25px rgba(180, 100, 255, 0.9); }
            100% { box-shadow: 0 0 12px rgba(100, 150, 255, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
          }
          .custom-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .cancel-button:hover:not(:disabled) {
            background-color: #B0BEC5;
          }
        `}
      </style>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <div style={styles.modalContent}>
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton style={styles.modalHeader}>
              <Modal.Title style={styles.modalTitle}>
                {selectedQuestion ? 'Edit Question' : 'Add New Question'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body style={styles.modalBody}>
              <Form.Group className="mb-2">
                <Form.Label style={styles.formLabel}>Question Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="questionText"
                  value={formData.questionText}
                  onChange={handleInputChange}
                  required
                  style={styles.formControl}
                  isInvalid={!!errors.questionText}
                />
                <Form.Control.Feedback type="invalid">{errors.questionText}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label style={styles.formLabel}>Options</Form.Label>
                <div style={styles.optionsGrid}>
                  <div>
                    {firstRowOptions.map((option, index) => (
                      <div key={index} style={styles.optionRow}>
                        <Form.Control
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          required={index < 2}
                          style={styles.formControl}
                        />
                        {index >= 4 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeOption(index)}
                            style={{ minWidth: '70px', padding: '0.25rem 0.5rem' }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div>
                    {secondRowOptions.map((option, index) => (
                      <div key={index + optionsPerRow} style={styles.optionRow}>
                        <Form.Control
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(index + optionsPerRow, e.target.value)}
                          placeholder={`Option ${index + optionsPerRow + 1}`}
                          required={index + optionsPerRow < 2}
                          style={styles.formControl}
                        />
                        {index + optionsPerRow >= 4 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeOption(index + optionsPerRow)}
                            style={{ minWidth: '70px', padding: '0.25rem 0.5rem' }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {errors.options && (
                  <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.options}
                  </div>
                )}
                {formData.options.length < 6 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={addOption}
                    style={styles.addOptionButton}
                  >
                    Add Option (Max 6)
                  </Button>
                )}
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label style={styles.formLabel}>Correct Answer</Form.Label>
                <Form.Select
                  name="correctAnswer"
                  value={formData.correctAnswer}
                  onChange={handleInputChange}
                  required
                  style={styles.formSelect}
                  isInvalid={!!errors.correctAnswer}
                >
                  <option value="">Select correct answer</option>
                  {formData.options.filter(opt => opt.trim() !== '').map((option, index) => (
                    <option key={index} value={option}>
                      {option || `Option ${index + 1}`}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.correctAnswer}</Form.Control.Feedback>
              </Form.Group>

              <div style={styles.sectionDifficultyGrid}>
                <Form.Group style={styles.sectionField}>
                  <Form.Label style={styles.formLabel}>Section</Form.Label>
                  <Form.Select
                    name="section"
                    value={formData.section}
                    onChange={handleInputChange}
                    required
                    style={styles.formSelect}
                    isInvalid={!!errors.section}
                  >
                    <option value="">Select a section</option>
                    <option value="math">Math</option>
                    <option value="science">Science</option>
                    <option value="history">History</option>
                    <option value="english">English</option>
                    <option value="geography">Geography</option>
                    <option value="other">Other</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.section}</Form.Control.Feedback>
                </Form.Group>

                {formData.section === 'other' && (
                  <Form.Group style={styles.sectionField}>
                    <Form.Label style={styles.formLabel}>Custom Section</Form.Label>
                    <Form.Control
                      type="text"
                      name="customSection"
                      value={formData.customSection}
                      onChange={handleCustomSectionChange}
                      required
                      placeholder="Enter custom section"
                      style={styles.formControl}
                      isInvalid={!!errors.customSection}
                    />
                    <Form.Control.Feedback type="invalid">{errors.customSection}</Form.Control.Feedback>
                  </Form.Group>
                )}

                <Form.Group style={styles.difficultyField}>
                  <Form.Label style={styles.formLabel}>Difficulty</Form.Label>
                  <Form.Select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                    required
                    style={styles.formSelect}
                    isInvalid={!!errors.difficulty}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.difficulty}</Form.Control.Feedback>
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer style={{ borderTop: 'none', padding: '0.75rem 1rem' }}>
              <Button
                style={styles.cancelButton}
                onClick={handleClose}
                className="cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                style={styles.button}
                className="custom-button"
              >
                {selectedQuestion ? 'Update' : 'Save'}
              </Button>
            </Modal.Footer>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default QuestionModal;